<template>
  <div v-loading="loading" class="nx-page nx-report-page kanban-annual-indicators">
    <nx-report-header title="年度指标全景看板">
      <div class="kb-header-actions">
        <el-select v-model="year" size="small" style="width: 120px" @change="loadData">
          <el-option v-for="y in yearOptions" :key="y" :label="y + ' 年度'" :value="String(y)" />
        </el-select>
        <el-radio-group v-model="dimension" size="small" @change="loadData">
          <el-radio-button label="transport">运输方式</el-radio-button>
          <el-radio-button label="category">货物品类</el-radio-button>
          <el-radio-button label="customer">客户</el-radio-button>
        </el-radio-group>
        <el-button size="small" icon="el-icon-refresh" @click="loadData">刷新</el-button>
      </div>
    </nx-report-header>

    <div class="kb-kpi-row cols-3">
      <div class="kb-kpi-card">
        <div class="kb-kpi-label">年度总任务值</div>
        <div class="kb-kpi-value">{{ fmtTon(block.totalPlan) }} 吨</div>
      </div>
      <div class="kb-kpi-card">
        <div class="kb-kpi-label">实际完成量</div>
        <div class="kb-kpi-value">{{ fmtTon(block.totalActual) }} 吨</div>
      </div>
      <div class="kb-kpi-card">
        <div class="kb-kpi-label">总体完成率</div>
        <div class="kb-kpi-value">{{ totalRate }}%</div>
      </div>
    </div>

    <div class="kb-charts-row">
      <div class="kb-chart-card">
        <div class="kb-chart-head">
          <span>运输量结构 · 完成率双环图</span>
        </div>
        <div ref="ring" class="kb-chart-box tall" />
      </div>
      <div class="kb-chart-card">
        <div class="kb-chart-head">
          <span>{{ progressTitle }}</span>
        </div>
        <div class="kb-progress-list">
          <div v-for="item in block.items" :key="item.name" class="kb-progress-item">
            <div class="kb-progress-label" :title="item.name">{{ item.name }}</div>
            <div class="kb-progress-bar-bg">
              <div
                class="kb-progress-fill"
                :style="{ width: pct(item) + '%', background: colorFor(item.name) }"
              />
            </div>
            <div class="kb-progress-pct">{{ pct(item) }}%</div>
            <div class="kb-yoy" :class="yoyClass(item)">{{ yoyText(item) }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import NxReportHeader from '@/components/nx/report/NxReportHeader.vue'
import { fetchAnnualIndicators } from './api'

const MIN_PIE_RENDER_VALUE = 0.000001

export default {
  name: 'KanbanAnnualIndicators',
  components: { NxReportHeader },
  data() {
    const cy = new Date().getFullYear()
    return {
      loading: false,
      year: String(cy),
      dimension: 'transport',
      block: { totalPlan: 0, totalActual: 0, items: [] },
      ringChartInstance: null
    }
  },
  computed: {
    yearOptions() {
      const cy = new Date().getFullYear()
      const list = []
      for (let y = cy + 1; y >= cy - 5; y--) list.push(y)
      return list
    },
    totalRate() {
      const p = Number(this.block.totalPlan) || 0
      if (p <= 0) return '0.0'
      return (((Number(this.block.totalActual) || 0) / p) * 100).toFixed(1)
    },
    progressTitle() {
      if (this.dimension === 'customer') return '各客户完成率明细'
      if (this.dimension === 'transport') return '各运输方式完成率明细'
      return '各货物品类完成率明细'
    }
  },
  mounted() {
    this.loadData()
    window.addEventListener('resize', this.onResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
    if (this.ringChartInstance) this.ringChartInstance.dispose()
  },
  methods: {
    fmtTon(v) {
      const n = Number(v) || 0
      if (n >= 10000) return (n / 10000).toFixed(1) + '万'
      return String(Math.round(n))
    },
    pct(item) {
      const p = Number(item.plan) || 0
      if (p <= 0) return 0
      return Number((((Number(item.actual) || 0) / p) * 100).toFixed(1))
    },
    colorFor(name) {
      if (this.dimension === 'transport') {
        const transportColors = {
          汽运到达: '#3b82f6',
          汽运发送: '#f97316',
          汽运发出: '#f97316',
          铁路到达: '#10b981',
          铁运至电厂量: '#8b5cf6'
        }
        if (transportColors[name]) return transportColors[name]
        const colors = ['#8b5cf6', '#ef4444', '#06b6d4', '#84cc16', '#f59e0b']
        let h = 0
        for (let i = 0; i < name.length; i++) h = (h << 5) - h + name.charCodeAt(i)
        return colors[Math.abs(h) % colors.length]
      }
      if (this.dimension === 'category') {
        if (name.includes('动力煤')) return '#3b82f6'
        if (name.includes('焦煤')) return '#f97316'
        if (name.includes('无烟煤')) return '#8b5cf6'
        return '#10b981'
      }
      const colors = ['#3b82f6', '#f97316', '#10b981', '#a855f7', '#ef4444']
      let h = 0
      for (let i = 0; i < name.length; i++) h = (h << 5) - h + name.charCodeAt(i)
      return colors[Math.abs(h) % colors.length]
    },
    yoyText(item) {
      if (item.yoyRate == null) return '—'
      const r = item.yoyRate
      const sym = r >= 0 ? '▲' : '▼'
      return `${sym} ${Math.abs(r).toFixed(1)}%`
    },
    yoyClass(item) {
      if (item.yoyRate == null) return ''
      return item.yoyRate >= 0 ? 'is-up' : 'is-down'
    },
    async loadData() {
      this.loading = true
      try {
        this.block = await fetchAnnualIndicators({ year: this.year, dimension: this.dimension })
        this.$nextTick(() => this.renderRing())
      } catch (e) {
        this.$message.error((e && e.message) || '加载失败')
      } finally {
        this.loading = false
      }
    },
    onResize() {
      this.ringChartInstance && this.ringChartInstance.resize()
    },
    renderRing() {
      if (this.ringChartInstance) this.ringChartInstance.dispose()
      this.ringChartInstance = echarts.init(this.$refs.ring)
      const data = this.block
      const items = data.items || []
      if (!items.length) {
        this.ringChartInstance.setOption({
          title: { text: '暂无数据', left: 'center', top: 'middle', textStyle: { color: '#94a3b8', fontSize: 14 } }
        })
        return
      }
      const renderPieValue = (value) => {
        const n = Number(value) || 0
        return n === 0 ? MIN_PIE_RENDER_VALUE : n
      }
      const actualTotal = items.reduce((sum, item) => sum + (Number(item.actual) || 0), 0)
      const innerData = items.map((item) => ({
        name: item.name,
        value: renderPieValue(item.actual),
        rawActual: Number(item.actual) || 0,
        actualPercent: actualTotal > 0 ? (((Number(item.actual) || 0) / actualTotal) * 100).toFixed(1) : '0.0',
        itemStyle: { color: this.colorFor(item.name) }
      }))
      const outerData = items.map((item) => {
        const pr = Number(item.plan) || 0
        const ratio = pr > 0 ? ((Number(item.actual) || 0) / pr) * 100 : 0
        return {
          name: `${item.name} 完成率`,
          value: renderPieValue(ratio),
          completionPercent: this.pct(item),
          itemStyle: { color: this.colorFor(item.name) }
        }
      })
      const tp = Number(data.totalPlan) || 0
      const tr = tp > 0 ? (((Number(data.totalActual) || 0) / tp) * 100).toFixed(1) : '0'
      this.ringChartInstance.setOption({
        tooltip: {
          trigger: 'item',
          formatter: (params) => {
            const d = params.data
            if (params.seriesName === '运输量占比') {
              return `${params.name}<br/>量: ${d.rawActual}<br/>占比: ${d.actualPercent}%`
            }
            return `${params.name}<br/>完成率: ${d.completionPercent}%`
          }
        },
        legend: { orient: 'vertical', left: 'left', data: items.map((i) => i.name) },
        series: [
          {
            name: '运输量占比',
            type: 'pie',
            radius: ['0%', '55%'],
            center: ['50%', '55%'],
            data: innerData,
            label: { formatter: (p) => `${p.name}: ${p.data.actualPercent}%`, fontSize: 11 }
          },
          {
            name: '完成率对比',
            type: 'pie',
            radius: ['65%', '85%'],
            center: ['50%', '55%'],
            data: outerData,
            label: {
              formatter: (p) => `${String(p.name).split(' ')[0]} ${p.data.completionPercent}%`,
              fontSize: 11
            }
          }
        ],
        graphic: [
          {
            type: 'text',
            left: 'center',
            top: '48%',
            style: {
              text: `总完成率\n${tr}%`,
              fill: '#1e293b',
              fontSize: 14,
              fontWeight: 'bold',
              textAlign: 'center'
            }
          }
        ]
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import './index.scss';
</style>
